import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { axios } from "../../../../../../Global";
import { setAppError } from "../../../../../../store/userSlice";
import PromptElement from "../../../../PromptElement";
import Highlighter from "react-highlight-words";
import JobEnquiryDownloadPopup from "./JobEnquiryDownloadPopup";
import { AiOutlineCloudDownload, AiOutlineEdit } from "react-icons/ai";
import { MdDeleteOutline } from "react-icons/md";
import sectionTableReport from "../excel/SectionTableReport";
import SearchFilter from "../../../../../../components/SmartDataTable/SearchFilter";
import PaginationControls from "../../../../../../components/SmartDataTable/PaginationControls";

export const InitialSelectedTItles = [
  { col: "Actual Profession", title: "actual_profession" },
  { col: "Visa Profession", title: "visa_profession" },
  { col: "Profession Level", title: "profession_level" },
  { col: "Gender", title: "gender" },
  { col: "Nationality", title: "nationality" },
  { col: "Quantity", title: "quantity" },
  { col: "Total Monthly Per Head Cost", title: "total_monthly_cost_head" },
];

export const CalculateQuantity = (arrayBasic) => {
  const regex = /\d+/;
  let total = 0;
  arrayBasic?.forEach((item) => {
    if (regex.test(item.quantity)) total += Number(item.quantity);
  });
  return total;
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function ListDataProjectJobEnquiry({
  loading,
  titles,
  jobEnquiries = [],
  setJobEnquiryDetails,
  nationality,
}) {
  const [jobEnquiriesData, setJobEnquiriesData] = useState(jobEnquiries);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const dispatch = useDispatch();

  useEffect(() => { setJobEnquiriesData(jobEnquiries); }, [jobEnquiries]);

  const [downloadPopupState, setDownloadPopupState] = useState({ open: false, rowId: null });
  const [showPromptElement, setShowPromptElement] = useState({ open: false, id: null });
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const handleDelete = useCallback(async (jeID) => {
    try {
      const res = await axios.delete(`/dashboard/project-job-enquiry/${jeID}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 200) {
        setJobEnquiriesData((prev) => prev.filter((item) => item.id !== jeID));
        dispatch(setAppError({ msg: res.data.message, color: "success" }));
      }
    } catch (error) {
      dispatch(setAppError({ msg: error?.response?.data?.message }));
    }
  }, [token, dispatch]);

  const searchWords = searchTerm ? searchTerm.trim().split(/\s+/) : [];

  const filtered = useMemo(() => searchTerm.trim()
    ? jobEnquiriesData.filter((item) =>
        ["job_enquiry_number", "name", "description"].some((k) =>
          String(item[k] ?? "").toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    : jobEnquiriesData,
  [jobEnquiriesData, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pageData = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const handleSearchChange = (val) => { setSearchTerm(val); setCurrentPage(1); };
  const handlePageChange = (p) => setCurrentPage(p);
  const handlePageSizeChange = (s) => { setPageSize(s); setCurrentPage(1); };

  const reportHeadings = [
    { title: "JOB ENQUIRY NO", key: "job_enquiry_number" },
    { title: "JOB ENQUIRY NAME", key: "name" },
    { title: "DESCRIPTION", key: "description" },
    { title: "NATIONALITY", key: "nationality_id", type: "array", array: nationality, arrayKey: "name" },
    { title: "TOTAL QUANTITY", key: "project_job_enquiry_cost_sheet_profession_basic_information", type: "function", function: CalculateQuantity },
  ];

  return (
    <div className="p-2">
      {downloadPopupState.open && (
        <JobEnquiryDownloadPopup
          InitialSelectedTItles={InitialSelectedTItles}
          openGenerateQuotation={downloadPopupState}
          setOpenGenerateQuotation={setDownloadPopupState}
        />
      )}
      {showPromptElement?.open && (
        <PromptElement
          showPromptElement={showPromptElement}
          setShowPromptElement={setShowPromptElement}
          deleteHandler={handleDelete}
        />
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 items-center w-full mb-4 flex-row-reverse">
        <SearchFilter
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          placeholder="Search across all columns..."
        />
        <div className="flex gap-2 items-center">
          <button
            className="p-2 px-4 rounded-md bg-blue disabled:bg-gray text-white min-w-fit text-sm flex items-center gap-2"
            onClick={() => sectionTableReport(filtered, reportHeadings, "Job Enquiry Report")}
          >
            Download
          </button>
          <button
            className="p-2 px-4 rounded-md bg-red text-white min-w-fit text-sm"
            onClick={() => navigate("create", { state: nationality })}
          >
            + Create New
          </button>
        </div>
      </div>

      {/* Slide hint */}
      <p className="text-sm w-full text-right font-medium mb-1 xl:hidden block">{`Slide / scroll to right >`}</p>

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-lg shadow max-h-[70vh]">
        <table className="min-w-full">
          <thead className="bg-gray-50 sticky top-0 z-20 shadow-sm">
            <tr className="bg-gray-50">
              {titles?.map((title, i) => (
                <th key={i} className="px-6 py-4 text-left text-base font-semibold text-gray-700 uppercase tracking-wider bg-gray-50" style={{ backgroundColor: '#f9fafb' }}>
                  {title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white">
            {loading && jobEnquiriesData.length === 0 ? (
              <tr>
                <td colSpan={titles?.length || 7} className="px-6 py-8 text-center">
                  <div className="flex items-center justify-center">
                    <span className="loading loading-spinner loading-md mr-2"></span>
                    Loading data...
                  </div>
                </td>
              </tr>
            ) : pageData.length === 0 ? (
              <tr>
                <td colSpan={titles?.length || 7} className="px-6 py-8 text-center text-gray-500">
                  {searchTerm ? `No results found for "${searchTerm}"` : "No Job Enquiry added."}
                </td>
              </tr>
            ) : (
              pageData.map((list, index) => (
                <tr key={list.id ?? index} className="border-b-[1.5px] border-slate-100 hover:bg-gray-50">
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">
                    {(safePage - 1) * pageSize + index + 1}
                  </td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">
                    <Highlighter searchWords={searchWords} autoEscape textToHighlight={list.job_enquiry_number || "-"} />
                  </td>
                  <td className="px-6 py-6 text-base text-gray-700">
                    <Highlighter searchWords={searchWords} autoEscape textToHighlight={list.name || "-"} />
                  </td>
                  <td className="px-6 py-6 text-base text-gray-700 max-w-xs" title={list.description}>
                    <Highlighter searchWords={searchWords} autoEscape textToHighlight={list.description || "-"} />
                  </td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">
                    <Highlighter searchWords={searchWords} autoEscape textToHighlight={nationality?.find((c) => c.id === list?.nationality_id)?.name || "-"} />
                  </td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700 font-medium">
                    {CalculateQuantity(list?.project_job_enquiry_cost_sheet_profession_basic_information)}
                  </td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">
                    <div className="flex gap-2">
                      <button title="Download" onClick={() => setDownloadPopupState({ open: true, rowId: list?.id })} className="p-1 px-2 rounded-md ring-1 ring-slate-600">
                        <AiOutlineCloudDownload size="1.4rem" />
                      </button>
                      <button title="Edit" onClick={() => { navigate("edit"); setJobEnquiryDetails({ id: list.id }); }} className="p-1 px-2 rounded-md ring-1 ring-slate-600">
                        <AiOutlineEdit size="1.4rem" />
                      </button>
                      <button title="Delete" onClick={() => setShowPromptElement({ open: true, id: list?.id })} className="p-1 px-2 rounded-md ring-1 ring-red text-white bg-red">
                        <MdDeleteOutline size="1.4rem" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <PaginationControls
        currentPage={safePage}
        totalPages={totalPages}
        pageSize={pageSize}
        totalRecords={filtered.length}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        isLoading={loading}
      />
    </div>
  );
}
